import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PopularityMaps {
  ownership: Map<string, number>;
  mentions: Map<string, number>;
}

/**
 * Aggregate community signals per card_id:
 * - ownership = how many user_collection rows reference the card
 * - mentions = how many gallery_posts reference the card
 */
export function useCardPopularity() {
  return useQuery({
    queryKey: ['card-popularity'],
    staleTime: 15 * 60 * 1000,
    queryFn: async (): Promise<PopularityMaps> => {
      const ownership = new Map<string, number>();
      const mentions = new Map<string, number>();

      // Paginated fetch of user_collection card_ids
      let page = 0;
      const PAGE = 1000;
      while (true) {
        const { data, error } = await supabase
          .from('user_collection')
          .select('card_id')
          .range(page * PAGE, (page + 1) * PAGE - 1);
        if (error) break;
        for (const r of data || []) {
          if (r.card_id) ownership.set(r.card_id, (ownership.get(r.card_id) || 0) + 1);
        }
        if (!data || data.length < PAGE) break;
        page++;
      }

      page = 0;
      while (true) {
        const { data, error } = await supabase
          .from('gallery_posts')
          .select('card_id')
          .range(page * PAGE, (page + 1) * PAGE - 1);
        if (error) break;
        for (const r of data || []) {
          if (r.card_id) mentions.set(r.card_id, (mentions.get(r.card_id) || 0) + 1);
        }
        if (!data || data.length < PAGE) break;
        page++;
      }

      return { ownership, mentions };
    },
  });
}
